/**
 * @file xcf-writer.ts
 * @description Binary writer for GIMP XCF format.
 */

import { encodeRle, mapBlendModeToXcf } from "./xcf-utils";

export class XcfWriter {
    private buffer: number[] = [];
    private offsets: Record<string, number> = {};

    write(doc: any): ArrayBuffer {
        const width = doc.width;
        const height = doc.height;
        const layers = doc.layers || [];

        // 1. Header
        this.writeString("gimp xcf v011\0");
        this.writeUint32(width);
        this.writeUint32(height);
        this.writeUint32(0); // Image Base Type: RGB

        // 2. Image Properties
        this.writeUint32(17); // PROP_COMPRESSION
        this.writeUint32(1);
        this.writeUint8(1);   // RLE
        this.writeUint32(0); // PROP_END
        this.writeUint32(0);

        // 3. Layer Pointers (Save these for later backfill)
        const layerPtrPos = this.buffer.length;
        for (let i = 0; i <= layers.length; i++) {
            this.writeUint64(0); // Placeholder
        }
        
        // 4. Channel Pointers (Empty)
        this.writeUint64(0);

        const realLayerPointers: number[] = [];

        // 5. Write Layers
        for (const layer of layers) {
            realLayerPointers.push(this.buffer.length);
            this.writeLayer(layer);
        }

        // BACKFILL: Layer Pointers
        let currentPos = layerPtrPos;
        for (const ptr of realLayerPointers) {
            this.insertUint64(currentPos, ptr);
            currentPos += 8;
        }

        return new Uint8Array(this.buffer).buffer;
    }

    private writeLayer(layer: any) {
        const can = layer.canvas as (HTMLCanvasElement | ImageData);
        const w = can.width;
        const h = can.height;

        this.writeUint32(w);
        this.writeUint32(h);
        this.writeUint32(1); // Type: RGBA
        this.writeXcfString(layer.name || "Layer");

        // Layer Properties
        this.writeProperty(6, 4, layer.opacity !== undefined ? Math.round(layer.opacity * 255) : 255);
        this.writeProperty(7, 4, layer.visible !== false ? 1 : 0);
        this.writeProperty(8, 4, mapBlendModeToXcf(layer.blendMode));
        this.writeProperty(15, 8, [layer.x || 0, layer.y || 0]); // PROP_OFFSETS
        this.writeUint32(0); // PROP_END
        this.writeUint32(0);

        const hierarchyPtr = this.buffer.length + 16; // Level offset
        this.writeUint64(hierarchyPtr);
        this.writeUint64(0); // No mask

        // Hierarchy
        this.writeUint32(w);
        this.writeUint32(h);
        this.writeUint32(4); // BPP
        const levelPtr = this.buffer.length + 8;
        this.writeUint64(levelPtr);

        // Level
        this.writeUint32(w);
        this.writeUint32(h);
        
        // Tiles
        const tilesWide = Math.ceil(w / 64);
        const tilesHigh = Math.ceil(h / 64);
        const tilePtrPos = this.buffer.length;
        for (let i = 0; i <= (tilesWide * tilesHigh); i++) this.writeUint64(0);

        const realTilePointers: number[] = [];
        const ctx = (can instanceof HTMLCanvasElement) ? can.getContext('2d') : null;

        for (let ty = 0; ty < tilesHigh; ty++) {
            for (let tx = 0; tx < tilesWide; tx++) {
                realTilePointers.push(this.buffer.length);
                const tw = Math.min(64, w - tx * 64);
                const th = Math.min(64, h - ty * 64);
                
                let pixels: Uint8ClampedArray;
                if (ctx) {
                    pixels = ctx.getImageData(tx * 64, ty * 64, tw, th).data;
                } else {
                    // Manual crop from ImageData
                    pixels = this.cropImageData(can as ImageData, tx * 64, ty * 64, tw, th);
                }

                this.writeTileRLE(pixels, tw, th);
            }
        }

        // Backfill Tiles
        let tp = tilePtrPos;
        for (const ptr of realTilePointers) {
            this.insertUint64(tp, ptr);
            tp += 8;
        }
    }

    private writeTileRLE(pixels: Uint8ClampedArray, tw: number, th: number) {
        // XCF planar RLE: RRR... GGG... BBB... AAA...
        const size = tw * th;
        for (let c = 0; c < 4; c++) {
            const channel = new Uint8Array(size);
            for (let i = 0; i < size; i++) channel[i] = pixels[i * 4 + c];
            const encoded = encodeRle(channel);
            for (let i = 0; i < encoded.length; i++) this.buffer.push(encoded[i]);
        }
    }

    private cropImageData(src: ImageData, x: number, y: number, w: number, h: number): Uint8ClampedArray {
        const out = new Uint8ClampedArray(w * h * 4);
        for (let i = 0; i < h; i++) {
            const srcIdx = ((y + i) * src.width + x) * 4;
            const dstIdx = i * w * 4;
            out.set(src.data.subarray(srcIdx, srcIdx + w * 4), dstIdx);
        }
        return out;
    }

    private writeProperty(type: number, len: number, data: any) {
        this.writeUint32(type);
        this.writeUint32(len);
        if (Array.isArray(data)) {
            for (const d of data) this.writeUint32(d);
        } else {
            this.writeUint32(data);
        }
    }

    private writeXcfString(s: string) {
        const bytes = new TextEncoder().encode(s + "\0");
        this.writeUint32(bytes.length);
        for (let i = 0; i < bytes.length; i++) this.buffer.push(bytes[i]);
    }

    private writeString(s: string) {
        const bytes = new TextEncoder().encode(s);
        for (let i = 0; i < bytes.length; i++) this.buffer.push(bytes[i]);
    }

    private writeUint8(v: number) { this.buffer.push(v & 0xFF); }
    private writeUint32(v: number) {
        this.buffer.push((v >> 24) & 0xFF, (v >> 16) & 0xFF, (v >> 8) & 0xFF, v & 0xFF);
    }
    private writeUint64(v: number) {
        // Simple 64-bit write for offsets (HCIE docs won't exceed 4GB)
        this.writeUint32(0); 
        this.writeUint32(v);
    }
    private insertUint64(pos: number, v: number) {
        this.buffer[pos + 4] = (v >> 24) & 0xFF;
        this.buffer[pos + 5] = (v >> 16) & 0xFF;
        this.buffer[pos + 6] = (v >> 8) & 0xFF;
        this.buffer[pos + 7] = v & 0xFF;
    }
}
